// errorMonitor.ts

import { sendMonitorEvent } from './pageTrack';

/**
 * 全局错误监控
 */
interface StackFrame {
  url: string;
  func: string;
  line: number;
  col: number;
}

/**
 * 构建堆栈信息数组（模拟浏览器堆栈格式）
 * 用于在 CDN 环境下，当错误对象没有 stack 时，手动构建堆栈信息
 */
const buildStackArray = (
  source: string | null,
  lineno: number | null,
  colno: number | null
): StackFrame[] => {
  const stack: StackFrame[] = [];

  if (source && lineno && colno) {
    stack.push({
      url: source,
      func: '?',
      line: lineno,
      col: colno,
    });
  }

  return stack;
};

/**
 * 将堆栈字符串转换为数组格式（监控 SDK 使用的格式）
 */
const parseStackToArray = (stackString: string | undefined): StackFrame[] | undefined => {
  if (!stackString) {
    return undefined;
  }

  const stack: StackFrame[] = [];
  const lines = stackString.split('\n');

  for (const stackLine of lines) {
    // 匹配格式：at functionName (url:line:col) 或 at url:line:col
    const match = stackLine.match(/at\s+(?:(.+?)\s+\()?([^\s]+):(\d+):(\d+)\)?/);
    if (match) {
      const func = match[1] || '?';
      const url = match[2];
      const lineNum = parseInt(match[3], 10);
      const col = parseInt(match[4], 10);

      stack.push({ url, func, line: lineNum, col });
    }
  }

  return stack.length > 0 ? stack : undefined;
};

/**
 * 检测是否是跨域脚本错误
 * 跨域脚本错误通常表现为：message 为 "Script error." 或堆栈信息被截断
 */
const isCrossOriginError = (
  message: string | Event,
  source: string | null | undefined,
  error: Error | null | undefined
): boolean => {
  const msg = String(message);
  // 检查是否是典型的跨域错误消息
  if (msg === 'Script error.' || msg === 'Script error') {
    return true;
  }

  // 检查 source 是否与当前页面不同源
  if (source) {
    try {
      const sourceUrl = new URL(source, window.location.href);
      const currentUrl = new URL(window.location.href);
      // 如果协议、域名或端口不同，则是跨域
      if (
        sourceUrl.origin !== currentUrl.origin &&
        sourceUrl.protocol !== 'blob:' &&
        sourceUrl.protocol !== 'data:'
      ) {
        return true;
      }
    } catch (e) {
      // URL 解析失败，可能是相对路径，不是跨域
    }
  }

  // 检查错误对象是否有堆栈，如果没有或很少，可能是跨域错误
  if (error && (!error.stack || error.stack.split('\n').length <= 2)) {
    return true;
  }

  return false;
};

export const setupGlobalErrorMonitor = () => {
  // 监听未捕获的 JavaScript 错误
  window.onerror = (message, source, lineno, colno, error) => {
    // 关键修复：跨域脚本错误堆栈被浏览器安全策略限制
    // 当 JS 文件在 CDN 域名上（跨域）时，浏览器会限制错误堆栈信息的获取
    // 这是浏览器的 Same-Origin Policy 安全机制导致的
    let preservedStack: string | undefined;
    let preservedStackArray: StackFrame[] | undefined;
    let errorWithPreservedStack: Error | undefined;
    const isCrossOrigin = isCrossOriginError(message, source || null, error || null);

    // 立即保存错误对象的 stack（在监控 SDK 处理之前）
    // 这是最关键的一步：在错误对象被处理之前立即保存堆栈信息
    if (error) {
      // 立即保存错误对象的 stack（如果存在）
      preservedStack = error.stack;
      // 尝试将堆栈字符串解析为数组格式
      if (preservedStack) {
        preservedStackArray = parseStackToArray(preservedStack);
      }
      errorWithPreservedStack = error;
    } else {
      // 如果没有 error 对象，创建一个新的错误对象
      errorWithPreservedStack = new Error(String(message));
    }

    // 关键修复：跨域脚本错误或堆栈被截断时的处理
    // 在跨域环境下，浏览器会限制错误堆栈信息的获取，导致堆栈被截断或丢失
    // 检查堆栈是否被截断：如果堆栈只有一行或很少，可能是被截断了
    const isStackTruncated = preservedStack && preservedStack.split('\n').length <= 2;

    if (!preservedStack || isStackTruncated) {
      // 方法1：如果堆栈被截断，尝试从 source、lineno、colno 补充堆栈信息
      if (source && lineno && colno) {
        const msg = String(message);
        const src = String(source);
        const line = Number(lineno);
        const col = Number(colno);

        // 如果已有堆栈但被截断，在开头添加错误发生的位置
        if (isStackTruncated && preservedStack) {
          // 在现有堆栈前添加错误发生的位置
          const existingStack = preservedStack.split('\n').slice(1).join('\n');
          preservedStack = `Error: ${msg}\n    at ${src}:${line}:${col}\n${existingStack}`;
        } else {
          // 如果没有堆栈，从 source、lineno、colno 构建
          preservedStack = `Error: ${msg}\n    at ${src}:${line}:${col}`;
        }

        // 同时构建数组格式的堆栈
        preservedStackArray = parseStackToArray(preservedStack);
        if (!preservedStackArray || preservedStackArray.length === 0) {
          preservedStackArray = buildStackArray(src, line, col);
        } else {
          // 如果已有堆栈数组但被截断，在开头添加错误发生的位置
          if (isStackTruncated) {
            preservedStackArray = [
              { url: src, func: '?', line, col },
              ...preservedStackArray,
            ];
          }
        }
      } else if (!preservedStack) {
        // 如果连 source、lineno、colno 都没有，至少记录错误消息
        const msg = String(message);
        preservedStack = `Error: ${msg}`;
      }

      // 方法2：尝试使用 Error.captureStackTrace（如果支持，如 V8 引擎）
      // 注意：这会在当前调用栈中添加当前函数，但至少能获取到部分堆栈信息
      // 只有在没有堆栈或堆栈被截断时才尝试
      if (!preservedStack || isStackTruncated) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ErrorConstructor = Error as any;
        if (typeof ErrorConstructor.captureStackTrace === 'function') {
          try {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            ErrorConstructor.captureStackTrace(
              errorWithPreservedStack,
              setupGlobalErrorMonitor
            );
            // 如果 captureStackTrace 成功，使用它生成的堆栈
            if (errorWithPreservedStack.stack) {
              // 移除当前函数和 setupGlobalErrorMonitor 的调用栈
              const stackLines = errorWithPreservedStack.stack.split('\n');
              // 保留从第2行开始的堆栈（跳过 "Error: message"）
              const relevantStack = stackLines.slice(2).join('\n');
              const msg = String(message);

              // 如果已有堆栈但被截断，合并两个堆栈
              if (isStackTruncated && preservedStack) {
                preservedStack = `${preservedStack}\n${relevantStack}`;
              } else {
                preservedStack = `Error: ${msg}\n${relevantStack}`;
              }

              // 解析为数组格式
              preservedStackArray = parseStackToArray(preservedStack);
            }
          } catch (e) {
            // 如果 captureStackTrace 失败，继续使用之前构建的堆栈
          }
        }
      }
    }

    // 确保 error 对象有 stack 属性
    if (errorWithPreservedStack && preservedStack) {
      const originalStackValue = preservedStack;
      Object.defineProperty(errorWithPreservedStack, 'stack', {
        get: () => originalStackValue,
        set: () => {
          // 阻止设置，保持原始堆栈
        },
        configurable: false,
        enumerable: true,
      });
    }

    // 将完整的堆栈信息直接放在 value 中，作为备用方案
    // 这样即使监控 SDK 重新捕获堆栈，我们也能从 value 中恢复完整的堆栈信息
    // 关键：同时传递字符串格式和数组格式的堆栈，确保监控 SDK 能够正确解析
    // 对于跨域错误，标记出来以便后续分析
    sendMonitorEvent(
      'GLOBAL_JS_ERROR',
      'error',
      '全局JS错误',
      {
        message,
        source,
        lineno,
        colno,
        // 标记是否是跨域错误
        isCrossOrigin,
        // 将完整的堆栈信息放在 value 中，确保不会丢失
        originalStack: preservedStack || error?.stack,
        // 同时传递 stack 字符串和数组格式
        stack: preservedStack || error?.stack,
        // 传递数组格式的堆栈（监控 SDK 可能使用这种格式）
        stackArray: preservedStackArray || parseStackToArray(error?.stack),
        originError: errorWithPreservedStack || error,
        // 跨域错误提示信息
        ...(isCrossOrigin
          ? {
            crossOriginHint:
                '跨域脚本错误：需要在 CDN 服务器上设置 Access-Control-Allow-Origin 响应头，' +
                '并在 script 标签上添加 crossorigin="anonymous" 属性',
          }
          : {}),
      },
    );
    return false; // 阻止默认错误处理
  };

  // 监听未处理的 Promise 错误
  window.onunhandledrejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason as Error | string | unknown;
    const reasonMessage = (reason as Error)?.message || String(reason || '');
    const reasonStack = (reason as Error)?.stack;
    sendMonitorEvent(
      'UNHANDLED_PROMISE_REJECTION',
      'error',
      '未处理的Promise拒绝',
      {
        reason: reasonMessage,
        stack: reasonStack,
      }
    );
    event.preventDefault(); // 阻止默认错误处理
  };
};
